// src/components/Planner/Planner.jsx
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { usePlanner } from "../../contexts/PlannerContext";
import { useStaff } from "../../contexts/StaffContext";
import { useNotifications } from "../../contexts/NotificationsContext";

import CalendarView from "./PlannerViews/CalendarView";
import GanttView from "./PlannerViews/GanttView";
import ListView from "./PlannerViews/ListView";
import KanbanView from "./PlannerViews/KanbanView";
import NewTaskModal from "./NewTaskModal";

import "./PlannerTheme.css"; // <-- theme tokens for dark/light

function getAssigneeId(ev) {
  // normalize across shapes (ev.assigneeId or ev.assignee)
  return ev?.assigneeId ?? ev?.assignee ?? "";
}

const LS_VIEW = "mudoPlannerView";
const LS_FILTER = "mudoPlannerAssigneeFilter";

export default function Planner() {
  const { events = [] } = usePlanner();
  const { staffList = [] } = useStaff();
  const { notifyAndInbox } = useNotifications() || {};
  const [searchParams] = useSearchParams();
  const deepLinkEventId = searchParams.get("eventId");
  const [openEventId, setOpenEventId] = useState(null);
  const [pendingEventId, setPendingEventId] = useState(null);
  const deepLinkHandledRef = useRef(false);
  const eventsLoadedRef = useRef(false);

  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(LS_VIEW) || "list";
    } catch {
      return "list";
    }
  }); // list | board | calendar | gantt

  const [assigneeFilter, setAssigneeFilter] = useState(() => {
    try {
      return localStorage.getItem(LS_FILTER) || "all";
    } catch {
      return "all";
    }
  });

  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => {
    if (!deepLinkEventId || deepLinkHandledRef.current) return;
    setPendingEventId(deepLinkEventId);
  }, [deepLinkEventId]);

  useEffect(() => {
    eventsLoadedRef.current = true;
  }, [events]);

  useEffect(() => {
    if (!pendingEventId || deepLinkHandledRef.current) return;
    const match = events.find((ev) => String(ev.id) === String(pendingEventId));
    if (match) {
      setOpenEventId(pendingEventId);
      return;
    }
    if (!eventsLoadedRef.current) return;
    notifyAndInbox?.({
      notification: {
        id: `planner-missing-${pendingEventId}`,
        title: "Task not found",
        message: "We couldn't find that planner task.",
        level: "warning",
        source: "planner",
        world: "studio",
        entity: { type: "task", id: pendingEventId },
        action: {
          kind: "NAVIGATE",
          to: "/dashboard/planner/tasks",
          params: { eventId: pendingEventId },
        },
      },
    });
    deepLinkHandledRef.current = true;
    setPendingEventId(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("eventId");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {}
  }, [events, notifyAndInbox, pendingEventId]);

  const handleDeepLinkHandled = useCallback(
    (found = true) => {
      if (!openEventId) return;
      if (!found) {
        notifyAndInbox?.({
          notification: {
            id: `planner-missing-${openEventId}`,
            title: "Task not found",
            message: "We couldn't find that planner task.",
            level: "warning",
            source: "planner",
            world: "studio",
            entity: { type: "task", id: openEventId },
            action: {
              kind: "NAVIGATE",
              to: "/dashboard/planner/tasks",
              params: { eventId: openEventId },
            },
          },
        });
      }
      setOpenEventId(null);
      setPendingEventId(null);
      deepLinkHandledRef.current = true;
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("eventId");
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
      } catch {}
    },
    [openEventId, notifyAndInbox]
  );

  useEffect(() => {
    try { localStorage.setItem(LS_VIEW, view); } catch {}
  }, [view]);

  useEffect(() => {
    try { localStorage.setItem(LS_FILTER, assigneeFilter); } catch {}
  }, [assigneeFilter]);

  const staffOptions = useMemo(
    () => [
      { id: "all", label: "All Staff" },
      { id: "unassigned", label: "Unassigned" },
      ...staffList.map((s) => ({
        id: String(s.id),
        label: s.name || String(s.id),
      })),
    ],
    [staffList]
  );

  const filteredEvents = useMemo(() => {
    if (assigneeFilter === "all") return events;
    if (assigneeFilter === "unassigned")
      return events.filter((e) => !String(getAssigneeId(e)));
    const wanted = String(assigneeFilter);
    return events.filter((e) => String(getAssigneeId(e)) === wanted);
  }, [events, assigneeFilter]);

  // quick counts per stage (for subtle feedback in header)
  const stageCounts = useMemo(() => {
    const map = new Map();
    for (const ev of filteredEvents) {
      const stage = (ev?.tags?.[0] || ev?.stage || "other").toString();
      map.set(stage, (map.get(stage) || 0) + 1);
    }
    return map;
  }, [filteredEvents]);

  return (
    <section className="planner space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Global Staff Filter (drives all views) */}
        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          className="px-3 py-1 text-sm"
          style={{
            background: "var(--pl-input-bg)",
            color: "var(--pl-text)",
            border: "1px solid var(--pl-border)",
            borderRadius: 8,
          }}
        >
          {staffOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>

        {/* Stage counters (compact) */}
        <div className="flex flex-wrap items-center gap-2 text-xs opacity-80">
          {[...stageCounts.entries()].slice(0, 6).map(([k, v]) => (
            <span
              key={k}
              className="px-2 py-0.5 rounded-full border"
              style={{
                borderColor: "var(--pl-border)",
                background: "var(--pl-chip-bg, transparent)",
              }}
              title={`${k}`}
            >
              {k}: {v}
            </span>
          ))}
        </div>

        {/* View toggle + New Task */}
        <div className="ml-auto flex gap-2">
          {[
            { id: "list", label: "List" },
            { id: "board", label: "Board" },
            { id: "calendar", label: "Calendar" },
            { id: "gantt", label: "Gantt" },
          ].map((v) => {
            const active = view === v.id;
            return (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={`btn ${active ? "btn-primary" : ""}`}
                title={`Switch to ${v.label}`}
              >
                {v.label}
              </button>
            );
          })}
          <button
            onClick={() => setNewOpen(true)}
            className="btn btn-primary"
            title="Create a new task (⌘/Ctrl+Enter to save in modal)"
          >
            + New Task
          </button>
        </div>
      </div>

      {/* Body — IMPORTANT: pass filteredEvents down */}
      {view === "calendar" && (
        <CalendarView
          events={filteredEvents}
          openEventId={openEventId}
          onDeepLinkHandled={handleDeepLinkHandled}
          key={`cal-${assigneeFilter}`}
        />
      )}
      {view === "gantt" && (
        <GanttView
          events={filteredEvents}
          openEventId={openEventId}
          onDeepLinkHandled={handleDeepLinkHandled}
          key={`gan-${assigneeFilter}`}
        />
      )}
      {view === "list" && (
        <ListView
          events={filteredEvents}
          openEventId={openEventId}
          onDeepLinkHandled={handleDeepLinkHandled}
          key={`lst-${assigneeFilter}`}
        />
      )}
      {view === "board" && (
        <KanbanView
          events={filteredEvents}
          openEventId={openEventId}
          onDeepLinkHandled={handleDeepLinkHandled}
          key={`kan-${assigneeFilter}`}
        />
      )}

      {/* Create modal */}
      {newOpen && <NewTaskModal isOpen={newOpen} onClose={() => setNewOpen(false)} />}
    </section>
  );
}
